// ============================================
// Interactive factory-test protocol (firmware `selftest ui`)
// ============================================
//
// Firmware v0.5.34+ streams the factory self-test as one NDJSON object per
// line (CRLF-terminated) when run as `selftest ui` / `selftest interactive`.
// These pure helpers parse that stream and fold it into a UI-friendly state
// object. The transport (electron serial) hands us `\n`-delimited lines with a
// trailing `\r` retained, and the console echoes the typed command terminated
// by a bare `\r` — so the first data line can arrive glued to the echo
// (`selftest ui\r{"t":"start"…}`). parseFactoryLine handles both.
//
// Line types we consume:
//   {"t":"start","mode":"interactive","fw":"v0.5.34","prompt_timeout_ms":60000}
//   {"t":"info","k":"mac","v":"E1A2B3C4D5F6"}
//   {"t":"result","id":1,"code":"SIT_TEST_0001","name":"vcore","pass":true,"v":3291,"unit":"mV","lo":1700,"hi":3600}
//   {"t":"step","name":"leds","v":" - Red LED on  (expect: RED)"}
//   {"t":"prompt","id":11,"code":"SIT_TEST_0011","name":"leds","q":"…","expect":"bool","timeout_ms":60000}
//   {"t":"ack","id":11,"answer":true}
//   {"t":"end","mask":"0x00000000","pass":true}

import { t } from "i18n";

// `label` is a getter so it is translated at read time, not at module load.
const factoryTest = (slug, rest) => ({
  slug,
  get label() {
    return t(`utils.factoryTest.tests.${slug}`);
  },
  ...rest,
});

/**
 * Ordered catalog of the factory-test suite, keyed by the stable `name` slug the
 * firmware emits. `interactive` tests pause for operator go/no-go; the rest are
 * automatic (the firmware decides pass/fail). Order drives the checklist.
 */
export const FACTORY_TESTS = [
  factoryTest("vcore", { interactive: false }),
  factoryTest("mcu_temp", { interactive: false }),
  factoryTest("usb", { interactive: false }),
  factoryTest("battery", { interactive: false }),
  factoryTest("charger", { interactive: false }),
  factoryTest("imu_lg", { interactive: false }),
  factoryTest("imu_hg", { interactive: false }),
  factoryTest("nor_id", { interactive: false }),
  factoryTest("nor_rw", { interactive: false }),
  factoryTest("button", { interactive: true, expect: "press" }),
  factoryTest("leds", { interactive: true, expect: "bool" }),
];

// Colour names the LED walk-through can announce, with a display hex.
export const LED_COLOR_HEX = {
  RED: "#EF4444",
  GREEN: "#22C55E",
  BLUE: "#3B82F6",
  WHITE: "#F5F5F4",
  YELLOW: "#EAB308",
  CYAN: "#06B6D4",
  MAGENTA: "#D946EF",
  ORANGE: "#F97316",
  AMBER: "#F59E0B",
  OFF: "#374151",
};

const normMac = (v) =>
  String(v || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

/**
 * Parse one received line into ALL of its NDJSON events. A "line" can carry
 * more than one object: the console echoes typed input without a newline
 * (`self{"t":"start"…}`), and two device writes can land in one read chunk
 * with only a `\r` between them (`{"t":"state"…}\r{"t":"ack"…}`). The old
 * parse-from-first-brace approach returned only the FIRST object and silently
 * discarded the rest — a real missed-event source for every consumer.
 *
 * This walks the line with a string-aware brace matcher (braces inside JSON
 * strings don't count), extracting every balanced top-level `{…}` and keeping
 * the ones that parse to an object with a string `t`. Junk between objects is
 * skipped. Returns [] for pure console noise.
 */
export function parseFactoryLines(raw) {
  if (raw == null) return [];
  const s = String(raw);
  const out = [];
  let i = s.indexOf("{");
  while (i !== -1 && i < s.length) {
    let depth = 0;
    let inStr = false;
    let esc = false;
    let end = -1;
    for (let j = i; j < s.length; j++) {
      const c = s[j];
      if (esc) {
        esc = false;
      } else if (inStr) {
        if (c === "\\") esc = true;
        else if (c === '"') inStr = false;
      } else if (c === '"') {
        inStr = true;
      } else if (c === "{") {
        depth += 1;
      } else if (c === "}") {
        depth -= 1;
        if (depth === 0) {
          end = j;
          break;
        }
      }
    }
    if (end === -1) break; // unbalanced tail (truncated line) — stop
    try {
      const obj = JSON.parse(s.slice(i, end + 1));
      if (obj && typeof obj === "object" && typeof obj.t === "string") {
        out.push(obj);
      }
    } catch {
      // not valid JSON — skip this candidate
    }
    i = s.indexOf("{", end + 1);
  }
  return out;
}

/**
 * First event on the line, or null. Kept for one-shot lookups; incremental
 * scanners should use parseFactoryLines so glued events aren't dropped.
 */
export function parseFactoryLine(raw) {
  const all = parseFactoryLines(raw);
  return all.length ? all[0] : null;
}

/** The exact bytes to answer a prompt: `1`/`0` + CR, on the same port. */
export function answerBytes(value) {
  return value ? "1\r" : "0\r";
}

/**
 * Pull the LED colour out of a `leds` step string, e.g.
 * " - Red LED on  (expect: RED)" -> { name: "RED", hex: "#EF4444" }.
 * Prefers the explicit `(expect: X)` annotation, else the first colour word.
 */
export function parseColorFromStep(v) {
  if (!v) return null;
  const s = String(v).toUpperCase();
  const names = Object.keys(LED_COLOR_HEX).join("|");
  const explicit = s.match(new RegExp(`EXPECT:\\s*(${names})`));
  const word = s.match(new RegExp(`\\b(${names})\\b`));
  const name = (explicit && explicit[1]) || (word && word[1]) || null;
  if (!name) return null;
  return { name, hex: LED_COLOR_HEX[name] };
}

/** Parse a hex mask string ("0x0000") to a number, or null. */
export function parseMask(mask) {
  if (mask == null) return null;
  const n = parseInt(String(mask), 16);
  return Number.isNaN(n) ? null : n;
}

/** Fresh, empty stream state. */
export function initFactoryState() {
  return {
    started: false,
    mode: null,
    fw: null,
    promptTimeoutMs: null,
    mac: null,
    tests: {}, // slug -> { status, pass, value, unit, lo, hi, code, id }
    ledSteps: [], // [{ text, color }] for the LED walk-through animation
    prompt: null, // { id, name, q, expect, timeoutMs, code } or null
    ended: null, // { mask, maskValue, pass } or null
  };
}

/**
 * Fold one parsed event into the running state (pure). Designed for
 * `events.reduce(reduceFactoryState, prev)`.
 */
export function reduceFactoryState(state, ev) {
  if (!ev || !ev.t) return state;

  switch (ev.t) {
    case "start":
      return {
        ...initFactoryState(),
        started: true,
        mode: ev.mode ?? null,
        fw: ev.fw ?? null,
        promptTimeoutMs: ev.prompt_timeout_ms ?? null,
      };

    case "info":
      if (ev.k === "mac") return { ...state, mac: normMac(ev.v) };
      return state;

    case "result": {
      if (!ev.name) return state;
      const tests = {
        ...state.tests,
        [ev.name]: {
          status: ev.pass ? "pass" : "fail",
          pass: !!ev.pass,
          value: ev.v ?? null,
          unit: ev.unit ?? null,
          lo: ev.lo ?? null,
          hi: ev.hi ?? null,
          code: ev.code ?? null,
          id: ev.id ?? null,
        },
      };
      // A result for the test we're prompting on (e.g. a real button press)
      // closes the prompt.
      const prompt =
        state.prompt && state.prompt.name === ev.name ? null : state.prompt;
      return { ...state, tests, prompt };
    }

    case "step": {
      if (ev.name === "leds") {
        return {
          ...state,
          ledSteps: [
            ...state.ledSteps,
            { text: ev.v ?? "", color: parseColorFromStep(ev.v) },
          ],
        };
      }
      return state;
    }

    case "prompt": {
      const prompt = {
        id: ev.id ?? null,
        name: ev.name ?? null,
        q: ev.q ?? "",
        expect: ev.expect ?? "bool",
        timeoutMs: ev.timeout_ms ?? state.promptTimeoutMs ?? null,
        code: ev.code ?? null,
      };
      const tests = ev.name
        ? {
            ...state.tests,
            [ev.name]: { ...(state.tests[ev.name] || {}), status: "running" },
          }
        : state.tests;
      return { ...state, prompt, tests };
    }

    case "ack": {
      // Device accepted an answer; close the prompt. For a bool prompt the
      // answer IS the result (LED). For a press prompt only a `0` (dead button)
      // resolves here as a fail — a real press resolves via a `result` line.
      if (!state.prompt) return state;
      const name = state.prompt.name;
      let tests = state.tests;
      if (name && state.prompt.expect === "bool") {
        tests = {
          ...tests,
          [name]: {
            ...(tests[name] || {}),
            status: ev.answer ? "pass" : "fail",
            pass: !!ev.answer,
          },
        };
      } else if (
        name &&
        state.prompt.expect === "press" &&
        ev.answer === false
      ) {
        tests = {
          ...tests,
          [name]: { ...(tests[name] || {}), status: "fail", pass: false },
        };
      }
      return { ...state, prompt: null, tests };
    }

    case "end":
      return {
        ...state,
        prompt: null,
        ended: {
          mask: ev.mask ?? null,
          maskValue: parseMask(ev.mask),
          pass: !!ev.pass,
        },
      };

    default:
      return state;
  }
}
